export interface HashtagLevel {
  levelNumber?: number;
  words: string[];
  initialLetters: string[] | string;
  difficulty?: string;
}

export type TileState = 'correct' | 'wrongPosition' | 'notInWord';

/** The 16 active positions in a # grid, in canonical order. */
export const ACTIVE_POSITIONS: readonly { row: number; col: number }[] = [
  { row: 0, col: 1 },
  { row: 0, col: 3 },
  { row: 1, col: 0 },
  { row: 1, col: 1 },
  { row: 1, col: 2 },
  { row: 1, col: 3 },
  { row: 1, col: 4 },
  { row: 2, col: 1 },
  { row: 2, col: 3 },
  { row: 3, col: 0 },
  { row: 3, col: 1 },
  { row: 3, col: 2 },
  { row: 3, col: 3 },
  { row: 3, col: 4 },
  { row: 4, col: 1 },
  { row: 4, col: 3 },
];

export const H1_INDICES = [2, 3, 4, 5, 6];
export const H2_INDICES = [9, 10, 11, 12, 13];
export const V1_INDICES = [0, 3, 7, 10, 14];
export const V2_INDICES = [1, 5, 8, 12, 15];
export const WORD_INDICES = [H1_INDICES, H2_INDICES, V1_INDICES, V2_INDICES];

export function parseInitialLetters(raw: string[] | string): string[] {
  if (Array.isArray(raw)) return raw.map((s) => s.charAt(0).toUpperCase());
  const trimmed = raw.trim();
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed.map((s: string) => s.charAt(0).toUpperCase());
  } catch {
    // fall through to comma-split
  }
  return trimmed
    .split(',')
    .map((s) => s.replace(/["'\s]/g, '').charAt(0).toUpperCase())
    .filter(Boolean);
}

export function evaluateTiles(tiles: string[], solution: string[]): TileState[] {
  const states: TileState[] = Array(16).fill('notInWord');

  for (let wordIdx = 0; wordIdx < 4; wordIdx++) {
    const indices = WORD_INDICES[wordIdx];
    const target = solution[wordIdx].split('');
    const counts = new Map<string, number>();
    for (const ch of target) counts.set(ch, (counts.get(ch) ?? 0) + 1);

    const wordStates: TileState[] = Array(5).fill('notInWord');

    // Greens
    for (let li = 0; li < 5; li++) {
      const ti = indices[li];
      if (tiles[ti] === target[li]) {
        wordStates[li] = 'correct';
        counts.set(tiles[ti], (counts.get(tiles[ti]) ?? 0) - 1);
      }
    }
    // Yellows
    for (let li = 0; li < 5; li++) {
      if (wordStates[li] === 'correct') continue;
      const ti = indices[li];
      const ch = tiles[ti];
      if ((counts.get(ch) ?? 0) > 0) {
        wordStates[li] = 'wrongPosition';
        counts.set(ch, (counts.get(ch) ?? 0) - 1);
      }
    }

    // Merge: max priority
    const priority: Record<TileState, number> = { correct: 2, wrongPosition: 1, notInWord: 0 };
    for (let li = 0; li < 5; li++) {
      const ti = indices[li];
      if (priority[wordStates[li]] > priority[states[ti]]) {
        states[ti] = wordStates[li];
      }
    }
  }

  return states;
}

export function completedWords(tiles: string[], solution: string[]): Set<number> {
  const set = new Set<number>();
  for (let w = 0; w < 4; w++) {
    const indices = WORD_INDICES[w];
    const target = solution[w].split('');
    if (indices.every((ti, li) => tiles[ti] === target[li])) set.add(w);
  }
  return set;
}

/**
 * 16-letter solved layout in `ACTIVE_POSITIONS` order, derived from the four
 * solution words (H1, H2, V1, V2). Mirrors iOS `HashtagPuzzleGenerator.solvedLetters`.
 */
export function solvedLetters(solution: string[]): string[] {
  const h1 = solution[0].split('');
  const h2 = solution[1].split('');
  const v1 = solution[2].split('');
  const v2 = solution[3].split('');
  return [
    v1[0], v2[0],
    h1[0], h1[1], h1[2], h1[3], h1[4],
    v1[2], v2[2],
    h2[0], h2[1], h2[2], h2[3], h2[4],
    v1[4], v2[4],
  ];
}

/**
 * Suggest the most useful swap to move toward the solution. Mirrors iOS
 * `HashtagPuzzleGenerator.findBestSwap`:
 *   1. A double-fix swap (both positions become correct).
 *   2. A single-fix swap that doesn't break a green.
 *   3. Last resort: any swap that fixes one slot.
 */
export function findBestSwap(
  current: string[],
  target: string[],
): { from: number; to: number } | null {
  const n = current.length;
  // Pass 1: double fix
  for (let i = 0; i < n; i++) {
    if (current[i] === target[i]) continue;
    for (let j = i + 1; j < n; j++) {
      if (current[j] === target[j]) continue;
      if (current[j] === target[i] && current[i] === target[j]) {
        return { from: i, to: j };
      }
    }
  }
  // Pass 2: single fix without breaking a green
  for (let i = 0; i < n; i++) {
    if (current[i] === target[i]) continue;
    for (let j = i + 1; j < n; j++) {
      if (current[j] === target[j]) continue;
      if (current[j] === target[i]) {
        return { from: i, to: j };
      }
    }
  }
  // Pass 3: last resort — accept a green-breaker (only safe if letters are identical)
  for (let i = 0; i < n; i++) {
    if (current[i] === target[i]) continue;
    for (let j = i + 1; j < n; j++) {
      if (current[j] === target[i]) {
        return { from: i, to: j };
      }
    }
  }
  return null;
}
